import React, { useCallback, useMemo, useState } from 'react';
import { siteConfig } from '@/config/site.config';
import { dictionaries } from './dictionaries';
import { I18nContext, type I18nContextValue } from './context';
import type { Locale } from './types';

const STORAGE_KEY = 'template-locale';

function readStoredLocale(): Locale | null {
    if (typeof window === 'undefined') return null;
    try {
        return window.localStorage.getItem(STORAGE_KEY);
    } catch {
        // Storage may be unavailable (private browsing, disabled cookies, SSR).
        return null;
    }
}

/**
 * Provides the current locale + `t()` translator to the component tree.
 *
 * This is a deliberately dependency-free scaffold (no `react-i18next`/`i18next`)
 * so it stays easy to audit and swap out. It is safe to always mount — when
 * `siteConfig.i18n.enabled` is `false` (the default), only the `en` dictionary
 * is ever used and no `LanguageSwitcher` is rendered, so behavior is unchanged
 * from a template with no i18n at all. See `docs/I18N.md`.
 */
export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { defaultLocale } = siteConfig.i18n;
    const [locale, setLocaleState] = useState<Locale>(() => readStoredLocale() ?? defaultLocale);

    const setLocale = useCallback((next: Locale) => {
        setLocaleState(next);
        try {
            window.localStorage.setItem(STORAGE_KEY, next);
        } catch {
            // Ignore write failures (private browsing, disabled storage, etc.).
        }
        if (typeof document !== 'undefined') {
            document.documentElement.lang = next;
        }
    }, []);

    const t = useCallback(
        (key: string, fallback?: string): string => {
            const dictionary = dictionaries[locale] ?? dictionaries[defaultLocale];
            return dictionary?.[key] ?? fallback ?? key;
        },
        [locale, defaultLocale],
    );

    const value = useMemo<I18nContextValue>(
        () => ({ locale, setLocale, t }),
        [locale, setLocale, t],
    );

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};
